// Print items for product barcode labels.
//
// A print item is one distinct label design: an aggregate variant (every label
// for the variant is identical) or one serialized inventory unit (its own
// barcode). Items that cannot produce a sellable label are kept — never
// silently dropped — and carry a `blockedReason` the print UI explains.

import { findOptionValue } from "./engine/barcodeLabelTemplate";

export const PRINT_ITEM_KINDS = Object.freeze({ VARIANT: "variant", UNIT: "unit" });

export const PRINT_BLOCK_REASONS = Object.freeze({
  // The variant has no barcode at all.
  MISSING_BARCODE: "missing_barcode",
  // Serialized stock sells by unit barcode; a variant-barcode label would be
  // rejected at the POS, so only unit labels may be printed.
  SERIALIZED_NEEDS_UNIT: "serialized_needs_unit",
  // The barcode contains characters CODE128 cannot encode.
  UNRENDERABLE_BARCODE: "unrenderable_barcode",
});

const toCount = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.max(0, Math.floor(numeric)) : 0;
};

export const isLiveCatalogVariant = (variant) => variant?.status !== "archived";

export const isSerializedProduct = (product) =>
  String(product?.inventoryMode || "").toLowerCase() === "serialized";

export const getPrintItemKey = (item) => {
  if (item?.printKey) return item.printKey;
  if (item?.kind === PRINT_ITEM_KINDS.UNIT) {
    return item.unitId != null ? `u:${item.unitId}` : `u:${item.barcode}`;
  }
  return `v:${item?.variantId}`;
};

const variantTitleFromOptions = (variant) =>
  variant.optionValues?.length
    ? variant.optionValues.map((optionValue) => optionValue.value).join(" / ")
    : variant.title;

const baseVariantFields = (variant, product) => ({
  variantId: variant.id,
  productId: product.id ?? variant.productId ?? null,
  productTitle: product.title || "",
  modelCode: product.baseSku || "",
  brandName: product.brandName || "",
  inventoryMode: isSerializedProduct(product) ? "serialized" : "aggregate",
  size: findOptionValue(variant.optionValues, "size"),
  color: findOptionValue(variant.optionValues, "color"),
  optionValues: variant.optionValues || [],
  variantTitle: variantTitleFromOptions(variant),
  price: variant.price,
  compareAtPrice: variant.compareAtPrice,
  sku: variant.sku || "",
  createdAt: variant.createdAt || null,
});

export const buildCatalogPrintItemFromVariant = (variant, product = {}) => {
  const summary = variant.inventory?.summary || {};
  const barcode = String(variant.barcode || "").trim();
  const item = {
    ...baseVariantFields(variant, product),
    kind: PRINT_ITEM_KINDS.VARIANT,
    barcode,
    onHand: toCount(summary.onHand),
    availableStock: toCount(summary.available),
  };

  let blockedReason = null;
  if (isSerializedProduct(product)) blockedReason = PRINT_BLOCK_REASONS.SERIALIZED_NEEDS_UNIT;
  else if (!barcode) blockedReason = PRINT_BLOCK_REASONS.MISSING_BARCODE;

  return { ...item, printKey: getPrintItemKey(item), blockedReason };
};

export const buildCatalogPrintItemFromUnit = (unit, variant, product = {}) => {
  const barcode = String(unit.barcode || "").trim();
  const item = {
    ...baseVariantFields(variant, product),
    kind: PRINT_ITEM_KINDS.UNIT,
    unitId: unit.id ?? null,
    barcode,
    // A serialized unit is exactly one physical item.
    onHand: 1,
    availableStock: String(unit.status || "").toLowerCase() === "available" ? 1 : 0,
    locationName: unit.locationName || null,
  };

  return {
    ...item,
    printKey: getPrintItemKey(item),
    blockedReason: barcode ? null : PRINT_BLOCK_REASONS.MISSING_BARCODE,
  };
};

/**
 * A variant described by a read model rather than a workspace (recent
 * variants, barcode resolution). Serialized variants are blocked: their labels
 * need unit barcodes, which these read models do not carry.
 */
export const buildCatalogPrintItemFromVariantSummary = ({
  variantId,
  productId = null,
  productTitle = "",
  brandName = "",
  inventoryMode,
  variantTitle,
  sku,
  barcode,
  price,
  compareAtPrice,
  inventory,
  createdAt = null,
}) => {
  const normalizedBarcode = String(barcode || "").trim();
  const serialized = String(inventoryMode || "").toLowerCase() === "serialized";
  const item = {
    kind: PRINT_ITEM_KINDS.VARIANT,
    variantId,
    productId,
    productTitle: productTitle || "",
    brandName: brandName || "",
    inventoryMode: serialized ? "serialized" : "aggregate",
    variantTitle: variantTitle || "",
    sku: sku || "",
    barcode: normalizedBarcode,
    price,
    compareAtPrice,
    onHand: toCount(inventory?.summary?.onHand),
    availableStock: toCount(inventory?.summary?.available),
    createdAt,
  };

  let blockedReason = null;
  if (serialized) blockedReason = PRINT_BLOCK_REASONS.SERIALIZED_NEEDS_UNIT;
  else if (!normalizedBarcode) blockedReason = PRINT_BLOCK_REASONS.MISSING_BARCODE;

  return { ...item, printKey: getPrintItemKey(item), blockedReason };
};

/** A label for one serialized unit resolved by scanning its barcode. */
export const buildCatalogPrintItemFromScannedUnit = (resolution) => {
  const unit = resolution?.inventoryUnit || {};
  const barcode = String(unit.barcode || "").trim();
  const item = {
    kind: PRINT_ITEM_KINDS.UNIT,
    unitId: unit.id ?? null,
    variantId: resolution?.variant?.id ?? null,
    productId: resolution?.product?.id ?? null,
    productTitle: resolution?.product?.title || "",
    brandName: resolution?.product?.brandName || "",
    inventoryMode: "serialized",
    variantTitle: resolution?.variant?.title || "",
    sku: resolution?.variant?.sku || "",
    barcode,
    price: resolution?.variant?.price,
    compareAtPrice: resolution?.variant?.compareAtPrice,
    onHand: 1,
    availableStock: String(unit.status || "").toLowerCase() === "available" ? 1 : 0,
    locationName: unit.locationName || null,
  };

  return {
    ...item,
    printKey: getPrintItemKey(item),
    blockedReason: barcode ? null : PRINT_BLOCK_REASONS.MISSING_BARCODE,
  };
};

/**
 * All label items for a product workspace: one per live aggregate variant, or
 * one per in-stock unit for serialized products.
 */
export const buildCatalogPrintItemsFromWorkspace = (workspace) => {
  const product = workspace?.product || {};
  const serialized = isSerializedProduct(product);

  return (workspace?.variants || []).filter(isLiveCatalogVariant).flatMap((variant) => {
    if (!serialized) return [buildCatalogPrintItemFromVariant(variant, product)];

    const units = variant.inventory?.units;
    // An older API without unit data cannot print serialized labels safely.
    if (!Array.isArray(units)) return [buildCatalogPrintItemFromVariant(variant, product)];
    return units.map((unit) => buildCatalogPrintItemFromUnit(unit, variant, product));
  });
};

/** Dedupes by label identity; blocked items are kept so they stay visible. */
export const mergeCatalogPrintItems = (items) => {
  const merged = [];
  const seen = new Set();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const key = getPrintItemKey(item);
    if (!item || seen.has(key)) return;
    seen.add(key);
    merged.push({ ...item, printKey: key });
  });

  return merged;
};

export const partitionPrintItems = (items) =>
  (Array.isArray(items) ? items : []).reduce(
    (result, item) => {
      if (item?.blockedReason) result.blocked.push(item);
      else result.printable.push(item);
      return result;
    },
    { printable: [], blocked: [] }
  );

export const formatCatalogPrintVariantDisplayLabel = (item) => {
  const tokens = [
    item?.productTitle,
    item?.color,
    item?.size,
    ...(Array.isArray(item?.optionValues) ? item.optionValues : [])
      .map((entry) => entry?.value)
      .filter(Boolean)
      .filter(
        (value) => value !== item?.color && value !== item?.size && value !== item?.productTitle
      ),
  ].filter(Boolean);

  return [...new Set(tokens)].join(" · ");
};
